import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Plus, Pencil, Eye, EyeOff, Truck, Store, UtensilsCrossed, Filter, Star, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import AdminLayout from './AdminLayout';

type TagType = 'cuisine' | 'ambiance' | 'service' | 'value_price' | 'dietary' | 'packaging' | 'delivery_speed' | 'food_quality_arrival' | 'highlights';
type OrderType = 'delivery' | 'takeout' | 'dine_in';

interface Tag {
  id: string;
  name: string;
  type: TagType;
  orderTypes: OrderType[];
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  showInFilter: boolean;
  showInReviewCreation: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

interface TagCategory {
  id: string;
  displayName: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const TAG_TYPE_CONFIG: Record<TagType, { label: string; description: string; color: string }> = {
  cuisine: {
    label: 'Cuisine',
    description: 'Tags for cuisine types matching brandCategoryName (e.g., Italian, Mexican, Chinese)',
    color: 'bg-amber-500 dark:bg-amber-600',
  },
  ambiance: {
    label: 'Ambiance',
    description: 'Tags for restaurant atmosphere (e.g., Cozy, Romantic, Lively)',
    color: 'bg-purple-500 dark:bg-purple-600',
  },
  service: {
    label: 'Service',
    description: 'Tags for service quality (e.g., Fast, Friendly, Attentive)',
    color: 'bg-blue-500 dark:bg-blue-600',
  },
  value_price: {
    label: 'Value / Price',
    description: 'Tags for value perception (e.g., Good Value, Expensive, Budget-Friendly)',
    color: 'bg-green-500 dark:bg-green-600',
  },
  dietary: {
    label: 'Dietary',
    description: 'Tags for dietary options (e.g., Vegetarian, Gluten-Free, Vegan)',
    color: 'bg-orange-500 dark:bg-orange-600',
  },
  packaging: {
    label: 'Packaging',
    description: 'Tags for delivery/takeout packaging (e.g., Eco-Friendly, Well-Sealed, Hot)',
    color: 'bg-teal-500 dark:bg-teal-600',
  },
  delivery_speed: {
    label: 'Speed',
    description: 'Tags for delivery timing (e.g., Fast Delivery, On-Time, Late)',
    color: 'bg-cyan-500 dark:bg-cyan-600',
  },
  food_quality_arrival: {
    label: 'Food Quality',
    description: 'Tags for food condition on arrival (e.g., Fresh, Still Hot, Well-Packaged)',
    color: 'bg-rose-500 dark:bg-rose-600',
  },
  highlights: {
    label: 'Highlights',
    description: 'Special highlight tags for filtering (e.g., The Best, Top Rated, Trending Now)',
    color: 'bg-yellow-500 dark:bg-yellow-600',
  },
};

const ORDER_TYPE_CONFIG: Record<OrderType, { label: string; icon: typeof Truck }> = {
  delivery: { label: 'Delivery', icon: Truck },
  takeout: { label: 'Takeout', icon: Store },
  dine_in: { label: 'Dine Out', icon: UtensilsCrossed },
};

function CategoryOrderRow({ 
  category, 
  config, 
  onUpdate 
}: { 
  category: TagCategory; 
  config?: { label: string; description: string; color: string }; 
  onUpdate: (updates: { sortOrder?: number; isActive?: boolean }) => void;
}) {
  const [localSortOrder, setLocalSortOrder] = useState(category.sortOrder.toString());
  
  const handleBlur = () => {
    const parsed = parseInt(localSortOrder, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed !== category.sortOrder) {
      onUpdate({ sortOrder: parsed });
    } else {
      setLocalSortOrder(category.sortOrder.toString());
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };
  
  return (
    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
      <div className="flex items-center gap-3">
        <Badge className={config?.color || 'bg-gray-500'}>
          {category.displayName}
        </Badge>
        {category.description && (
          <span className="text-sm text-muted-foreground">{category.description}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min="0"
          className="w-20 h-8"
          value={localSortOrder}
          onChange={(e) => setLocalSortOrder(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
        <Switch
          checked={category.isActive}
          onCheckedChange={(checked) => onUpdate({ isActive: checked })}
        />
      </div>
    </div>
  );
}

const TAG_TYPES: TagType[] = ['highlights', 'cuisine', 'ambiance', 'service', 'value_price', 'dietary', 'packaging', 'delivery_speed', 'food_quality_arrival'];
const ORDER_TYPES: OrderType[] = ['delivery', 'takeout', 'dine_in'];

export default function AdminTags() {
  const { toast } = useToast();
  const [showInactive, setShowInactive] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'ambiance' as TagType,
    orderTypes: ['delivery', 'takeout', 'dine_in'] as OrderType[],
    description: '',
    sortOrder: 0,
    showInFilter: true,
    showInReviewCreation: true,
  });
  const [activeTab, setActiveTab] = useState<'tags' | 'categories'>('tags');

  const { data: tags, isLoading } = useQuery<Tag[]>({
    queryKey: ['/api/admin/tags'],
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; type: TagType; orderTypes: OrderType[]; description?: string; sortOrder?: number; showInFilter?: boolean; showInReviewCreation?: boolean }) => {
      return apiRequest('POST', '/api/admin/tags', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tags'] });
      setIsCreateDialogOpen(false);
      resetForm();
      toast({ title: 'Tag created successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to create tag', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; type?: TagType; orderTypes?: OrderType[]; description?: string; sortOrder?: number; isActive?: boolean; showInFilter?: boolean; showInReviewCreation?: boolean }) => {
      return apiRequest('PATCH', `/api/admin/tags/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tags'] });
      setEditingTag(null);
      resetForm();
      toast({ title: 'Tag updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update tag', description: error.message, variant: 'destructive' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return apiRequest('PATCH', `/api/admin/tags/${id}`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tags'] });
      toast({ title: 'Tag status updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update tag', description: error.message, variant: 'destructive' });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'ambiance',
      orderTypes: ['delivery', 'takeout', 'dine_in'],
      description: '',
      sortOrder: 0,
      showInFilter: true,
      showInReviewCreation: true,
    });
  };

  // Tag Categories query and mutations
  const { data: categories, isLoading: categoriesLoading } = useQuery<TagCategory[]>({
    queryKey: ['/api/admin/tag-categories'],
  });

  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; displayName?: string; description?: string; sortOrder?: number; isActive?: boolean }) => {
      return apiRequest('PATCH', `/api/admin/tag-categories/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/tag-categories'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tags'] });
      toast({ title: 'Category updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Failed to update category', description: error.message, variant: 'destructive' });
    },
  });

  const getTypeColor = (type: TagType, isActive: boolean) => {
    if (!isActive) return 'bg-muted text-muted-foreground';
    return TAG_TYPE_CONFIG[type]?.color || 'bg-gray-500';
  };

  const filteredTags = showInactive ? tags : tags?.filter(t => t.isActive);

  const groupedTags = TAG_TYPES.reduce((acc, type) => {
    acc[type] = filteredTags?.filter(t => t.type === type) || [];
    return acc;
  }, {} as Record<TagType, Tag[]>);

  const handleOrderTypeToggle = (orderType: OrderType) => {
    setFormData(prev => {
      const newOrderTypes = prev.orderTypes.includes(orderType)
        ? prev.orderTypes.filter(t => t !== orderType)
        : [...prev.orderTypes, orderType];
      return { ...prev, orderTypes: newOrderTypes.length > 0 ? newOrderTypes : [orderType] };
    });
  };

  const handleCreateSubmit = () => {
    if (!formData.name.trim()) {
      toast({ title: 'Tag name is required', variant: 'destructive' });
      return;
    }
    createMutation.mutate({
      name: formData.name.trim(),
      type: formData.type,
      orderTypes: formData.orderTypes,
      description: formData.description.trim() || undefined,
      sortOrder: formData.sortOrder,
      showInFilter: formData.showInFilter,
      showInReviewCreation: formData.showInReviewCreation,
    });
  };

  const handleEditSubmit = () => {
    if (!editingTag || !formData.name.trim()) {
      toast({ title: 'Tag name is required', variant: 'destructive' });
      return;
    }
    updateMutation.mutate({
      id: editingTag.id,
      name: formData.name.trim(),
      type: formData.type,
      orderTypes: formData.orderTypes,
      description: formData.description.trim() || undefined,
      sortOrder: formData.sortOrder,
      showInFilter: formData.showInFilter,
      showInReviewCreation: formData.showInReviewCreation,
    });
  };

  const openEditDialog = (tag: Tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      type: tag.type,
      orderTypes: tag.orderTypes || ['delivery', 'takeout', 'dine_in'],
      description: tag.description || '',
      sortOrder: tag.sortOrder ?? 0,
      showInFilter: tag.showInFilter ?? true,
      showInReviewCreation: tag.showInReviewCreation ?? true,
    });
  };

  const renderOrderTypeIndicators = (orderTypes: OrderType[]) => {
    return (
      <span className="flex items-center gap-0.5 ml-1">
        {orderTypes.includes('delivery') && <Truck className="w-2.5 h-2.5" />}
        {orderTypes.includes('takeout') && <Store className="w-2.5 h-2.5" />}
        {orderTypes.includes('dine_in') && <UtensilsCrossed className="w-2.5 h-2.5" />}
      </span>
    );
  };

  const renderOrderTypesSection = () => (
    <div className="space-y-2">
      <Label>Applies To</Label>
      <div className="flex flex-wrap gap-4">
        {ORDER_TYPES.map((orderType) => {
          const config = ORDER_TYPE_CONFIG[orderType];
          const Icon = config.icon;
          return (
            <div key={orderType} className="flex items-center gap-2">
              <Checkbox
                id={`orderType-${orderType}`}
                checked={formData.orderTypes.includes(orderType)}
                onCheckedChange={() => handleOrderTypeToggle(orderType)}
              />
              <Label
                htmlFor={`orderType-${orderType}`}
                className="flex items-center gap-1.5 text-sm font-normal cursor-pointer"
              >
                <Icon className="w-4 h-4" />
                {config.label}
              </Label>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        Select which order types this tag will be shown for during review creation
      </p>
    </div>
  );

  const renderVisibilitySection = () => (
    <div className="space-y-3">
      <Label>Visibility</Label>
      <div className="space-y-3 p-3 bg-muted rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Show in Filter</p>
              <p className="text-xs text-muted-foreground">Display this tag in filter options</p>
            </div>
          </div>
          <Switch
            checked={formData.showInFilter}
            onCheckedChange={(checked) => setFormData({ ...formData, showInFilter: checked })}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Show in Review Creation</p>
              <p className="text-xs text-muted-foreground">Allow users to select this tag when creating reviews</p>
            </div>
          </div>
          <Switch
            checked={formData.showInReviewCreation}
            onCheckedChange={(checked) => setFormData({ ...formData, showInReviewCreation: checked })}
          />
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-page-title">Tags & Categories</h1>
            <p className="text-muted-foreground">Manage tags used in reviews</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-inactive"
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <Label htmlFor="show-inactive" className="text-sm">Show inactive</Label>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)} data-testid="button-add-tag">
              <Plus className="w-4 h-4 mr-2" />
              Add Tag
            </Button>
          </div>
        </div>

        {/* Category Order Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GripVertical className="w-5 h-5 text-muted-foreground" />
              Category Display Order
            </CardTitle>
            <CardDescription>
              Manage the order in which tag categories appear in filters and review creation. Lower sort order values appear first. Change sort order and press Enter or click outside to save.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {categoriesLoading ? (
              <p className="text-muted-foreground">Loading categories...</p>
            ) : !categories || categories.length === 0 ? (
              <p className="text-muted-foreground">No categories found</p>
            ) : (
              <div className="space-y-2">
                {categories.sort((a, b) => a.sortOrder - b.sortOrder).map((category) => (
                  <CategoryOrderRow
                    key={category.id}
                    category={category}
                    config={TAG_TYPE_CONFIG[category.id as TagType]}
                    onUpdate={(updates) => updateCategoryMutation.mutate({ id: category.id, ...updates })}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {TAG_TYPES.map((type) => {
          const config = TAG_TYPE_CONFIG[type];
          const typeTags = groupedTags[type];
          return (
            <Card key={type}>
              <CardHeader>
                <CardTitle>{config.label} Tags</CardTitle>
                <CardDescription>{config.description}</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <p className="text-muted-foreground">Loading...</p>
                ) : typeTags.length === 0 ? (
                  <p className="text-muted-foreground">
                    {showInactive ? `No ${config.label.toLowerCase()} tags yet` : `No active ${config.label.toLowerCase()} tags`}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {typeTags.map((tag) => (
                      <div key={tag.id} className="group relative" data-testid={`tag-${tag.id}`}>
                        <Badge 
                          className={`${getTypeColor(type, tag.isActive)} gap-1 pr-1 cursor-pointer`}
                          onClick={() => openEditDialog(tag)}
                          title={`${tag.showInFilter === false ? 'Hidden from filters' : 'Shown in filters'} | ${tag.showInReviewCreation === false ? 'Hidden from review creation' : 'Shown in review creation'}`}
                        >
                          {tag.name}
                          {tag.orderTypes && tag.orderTypes.length < 3 && renderOrderTypeIndicators(tag.orderTypes)}
                          {(tag.showInFilter === false || tag.showInReviewCreation === false) && (
                            <span className="flex items-center gap-0.5 ml-0.5 opacity-60">
                              {tag.showInFilter === false && <Filter className="w-2.5 h-2.5 line-through" />}
                              {tag.showInReviewCreation === false && <Star className="w-2.5 h-2.5" />}
                            </span>
                          )}
                          <span className="text-xs opacity-70">({tag.usageCount})</span>
                          <span className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Pencil className="w-3 h-3" />
                          </span>
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute -right-6 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActiveMutation.mutate({ id: tag.id, isActive: !tag.isActive });
                          }}
                          title={tag.isActive ? 'Deactivate tag' : 'Activate tag'}
                        >
                          {tag.isActive ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="flex flex-col max-h-[90svh]">
            <DialogHeader>
              <DialogTitle>Add New Tag</DialogTitle>
              <DialogDescription>Create a new tag for reviews</DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 pr-1">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tag-name">Tag Name</Label>
                <Input
                  id="tag-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Cozy, Fast Service, Vegan"
                  data-testid="input-tag-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tag-type">Tag Category</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: TagType) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger id="tag-type" data-testid="select-tag-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TAG_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {TAG_TYPE_CONFIG[type].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {renderOrderTypesSection()}
              {renderVisibilitySection()}
              <div className="space-y-2">
                <Label htmlFor="tag-description">Description (optional)</Label>
                <Textarea
                  id="tag-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this tag..."
                  className="resize-none"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tag-sort-order">Sort Order</Label>
                <Input
                  id="tag-sort-order"
                  type="number"
                  min="0"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">Lower numbers appear first within the category</p>
              </div>
            </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setIsCreateDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateSubmit} 
                disabled={createMutation.isPending}
                data-testid="button-create-tag"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Tag'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!editingTag} onOpenChange={(open) => { if (!open) { setEditingTag(null); resetForm(); } }}>
          <DialogContent className="flex flex-col max-h-[90svh]">
            <DialogHeader>
              <DialogTitle>Edit Tag</DialogTitle>
              <DialogDescription>Update tag details</DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 pr-1">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-tag-name">Tag Name</Label>
                <Input
                  id="edit-tag-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="input-edit-tag-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-tag-type">Tag Category</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: TagType) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger id="edit-tag-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TAG_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {TAG_TYPE_CONFIG[type].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {renderOrderTypesSection()}
              {renderVisibilitySection()}
              <div className="space-y-2">
                <Label htmlFor="edit-tag-description">Description (optional)</Label>
                <Textarea
                  id="edit-tag-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="resize-none"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-tag-sort-order">Sort Order</Label>
                <Input
                  id="edit-tag-sort-order"
                  type="number"
                  min="0"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">Lower numbers appear first within the category</p>
              </div>

              {editingTag && (
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm font-medium">Tag Status</p>
                    <p className="text-xs text-muted-foreground">
                      {editingTag.isActive ? 'Active - visible to users' : 'Inactive - hidden from users'}
                    </p>
                  </div>
                  <Switch
                    checked={editingTag.isActive}
                    onCheckedChange={(checked) => {
                      toggleActiveMutation.mutate({ id: editingTag.id, isActive: checked });
                      setEditingTag({ ...editingTag, isActive: checked });
                    }}
                  />
                </div>
              )}
            </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditingTag(null); resetForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleEditSubmit} 
                disabled={updateMutation.isPending}
                data-testid="button-save-tag"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
